import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { EntryStatusEnum, EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import Grid from '@mui/material/Grid';
import { type Column, type Row } from '@tanstack/react-table';
import { deepEqual } from 'fast-equals';
import { type SetStateAction, useAtomValue, type WritableAtom } from 'jotai';
import { type RESET, selectAtom, useResetAtom } from 'jotai/utils';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { drawDefinitionConstants, tournamentEngine } from 'tods-competition-factory';

import { drawDefinitionsAtomFamily, eventAtomFamily } from 'atoms/events';
import { tournamentAtom } from 'atoms/tournament';
import StandardButton from 'components/buttons/StandardButton';
import FilteringNoResults from 'components/emptyStates/FilteringNoResults';
import { useStyles } from 'components/tables/competitors/alternates/styles';
import CompetitorsTableEmptyState from 'components/tables/competitors/CompetitorsTableEmptyState';
import CompetitorsTableSubtitle from 'components/tables/competitors/CompetitorsTableSubtitle';
import CompetitorsTableTitle from 'components/tables/competitors/CompetitorsTableTitle';
import {
  alternatePlayersTeamRequiredColumns,
  alternateTableRequiredColumns,
  alternateTeamsRequiredColumns,
} from 'components/tables/competitors/config';
import { useCompetitorsTableColumns } from 'components/tables/competitors/useCompetitorsTableColumns';
import { getOrderedParticipants } from 'components/tables/competitors/utils';
import DrawParticipantsTable from 'components/tables/eventsSplitDraws/drawParticipantsTable/DrawParticipantsTable';
import { BodyLargeLight } from 'components/typography/Typography';
import { usePlayersProfileDialog } from 'containers/dialogs/PlayersProfile';
import { usePromoteToMainDrawDialog } from 'containers/dialogs/PromoteToMainDraw';
import { useCompetitorsPopover } from 'containers/eventsSplitDraws/entries/EntriesActions';
import useMutateTournament from 'hooks/tournament/useMutateTournament';
import { useProgress } from 'hooks/tournament/useSaveTournament';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { isStandalone } from 'utils/environment';

import AlternatesBulkActions from './AlternatesBulkActions';

const { ALTERNATE } = EntryStatusEnum;
const { MAIN } = drawDefinitionConstants;

interface AlternatesTableProps {
  alternateParticipantsAtom: WritableAtom<
    ParticipantInterface[],
    [typeof RESET | SetStateAction<ParticipantInterface[]>],
    void
  >;
  title?: string;
  alternateDrawParticipants: ParticipantInterface[];
  isQualifyingEnabled: boolean;
  isTeamTable?: boolean;
  isTeamPlayersTable?: boolean;
  tournamentId: string;
  onUnpairPlayers?: (rowItemId: string, participants: ParticipantInterface[]) => void;
  setEntryPositionsState: (entryPositions: { participantId: string; entryPosition: number }[]) => void;
  columnsSelection: Array<string>;
  saveColumnsSelection: (columns: Column<ParticipantInterface>[]) => void;
}

const AlternatesTable: React.FC<React.PropsWithChildren<AlternatesTableProps>> = ({
  alternateParticipantsAtom,
  title,
  alternateDrawParticipants,
  isQualifyingEnabled,
  isTeamTable,
  isTeamPlayersTable,
  tournamentId,
  onUnpairPlayers,
  setEntryPositionsState,
  columnsSelection,
  saveColumnsSelection,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { eventId } = useParams<{ eventId: string }>();
  const event = useAtomValue(eventAtomFamily(eventId));
  const drawDefinition = useAtomValue(drawDefinitionsAtomFamily(eventId))?.[0];
  const [moveTo, isLoadingMoveTo] = useMutateTournament('moveTo');
  const resetAlternateParticipants = useResetAtom(alternateParticipantsAtom);
  const [setEntryPositions, setEntryPositionsProgress] = useMutateTournament('setEntryPositions');
  const mainDrawInProgress = useProgress('moveToMainDraw');
  const openCompetitorsPopover = useCompetitorsPopover();
  const showProfileDialog = usePlayersProfileDialog();
  const openPromoteToMainDrawDialog = usePromoteToMainDrawDialog();
  const isDouble = useMemo(() => event?.eventType === EventTypeEnum.DOUBLES, [event?.eventType]);

  const getFlights = useCallback(
    () =>
      tournamentEngine.getFlightProfile({
        eventId: event?.eventId,
      })?.flightProfile?.flights,
    [event],
  );

  const flights = useAtomValue(selectAtom(tournamentAtom, getFlights, deepEqual));
  const [isEditReorder, setIsEditReorder] = useState<boolean>(false);
  const isStandAloneDemo = isStandalone();

  const handlePromoteAlternates = useCallback(
    (participantIds: string[], entryStage: 'MAIN' | 'QUALIFYING', resetRowSelection?: () => void) => {
      const selectedParticipants = alternateDrawParticipants.filter((alternate) =>
        participantIds.includes(alternate.participantId),
      );
      const promotePlayers = () => moveTo(participantIds, event, isTeamPlayersTable, entryStage);

      isStandAloneDemo
        ? promotePlayers()
        : openPromoteToMainDrawDialog({
            entryStage: entryStage,
            eventsIds: [event?.eventId],
            eventType: event?.eventType,
            loading: mainDrawInProgress || isLoadingMoveTo,
            promoteParticipants: selectedParticipants,
            tournamentId: tournamentId,
            onPromotePlayers: promotePlayers,
            onPromoteTeamPlayers: isTeamPlayersTable
              ? () => moveTo(participantIds, event, isTeamPlayersTable, MAIN)
              : undefined,
            onResetRowSelection: resetRowSelection,
          });
    },
    [
      alternateDrawParticipants,
      event,
      isLoadingMoveTo,
      isStandAloneDemo,
      isTeamPlayersTable,
      mainDrawInProgress,
      moveTo,
      openPromoteToMainDrawDialog,
      tournamentId,
    ],
  );

  const handleUnpairPlayers = useCallback(
    (participantId: string) => onUnpairPlayers(participantId, alternateDrawParticipants),
    [alternateDrawParticipants, onUnpairPlayers],
  );

  const handleOpenCompetitorsPopover = useCallback(
    (participant: ParticipantInterface, clickEvent: React.MouseEvent<HTMLSpanElement, MouseEvent>) =>
      openCompetitorsPopover(
        participant,
        { onPromote: handlePromoteAlternates, onUnpairPlayers: handleUnpairPlayers },
        clickEvent.currentTarget,
        event,
        isQualifyingEnabled,
      ),
    [event, handlePromoteAlternates, handleUnpairPlayers, isQualifyingEnabled, openCompetitorsPopover],
  );

  const requiredColumns = useMemo(() => {
    if (isTeamTable) return alternateTeamsRequiredColumns;
    if (isTeamPlayersTable) return alternatePlayersTeamRequiredColumns;
    return alternateTableRequiredColumns;
  }, [isTeamPlayersTable, isTeamTable]);

  const tableColumns = useCompetitorsTableColumns({
    classes,
    isEditReorder,
    isTeamTable,
    requiredColumns,
    event,
    flights,
    onClickAction: handleOpenCompetitorsPopover,
    onOpenPlayerProfile: showProfileDialog,
  });

  const handleSaveReorder = useCallback(async () => {
    const orderedParticipants = getOrderedParticipants(alternateDrawParticipants);
    await setEntryPositions({
      drawId: drawDefinition?.drawId,
      eventId,
      entryPositions: orderedParticipants,
      entryStatus: ALTERNATE,
    });
    resetAlternateParticipants();
    setIsEditReorder(false);
  }, [alternateDrawParticipants, drawDefinition?.drawId, eventId, resetAlternateParticipants, setEntryPositions]);

  const handleChangeEditMode = useCallback(() => {
    setIsEditReorder(!isEditReorder);
  }, [isEditReorder]);

  const renderBulkActions = useCallback(
    (selectedFlatRows: Row<ParticipantInterface>[], resetRowSelection?: () => void) => (
      <AlternatesBulkActions
        isQualifyingEnabled={isQualifyingEnabled}
        selectedFlatRows={selectedFlatRows}
        onPromote={handlePromoteAlternates}
        onResetRowSelection={resetRowSelection}
      />
    ),
    [handlePromoteAlternates, isQualifyingEnabled],
  );

  const additionalButtons = useMemo(
    () => (
      <Grid item>
        <Grid container direction="row" spacing={2}>
          <Grid item>
            <StandardButton
              loading={setEntryPositionsProgress}
              variant="primary"
              onClick={isEditReorder ? handleSaveReorder : handleChangeEditMode}
            >
              {isEditReorder ? t('save') : t('editOrder')}
            </StandardButton>
          </Grid>
        </Grid>
      </Grid>
    ),
    [setEntryPositionsProgress, isEditReorder, handleSaveReorder, handleChangeEditMode, t],
  );

  return (
    <>
      {!!title && (
        <CompetitorsTableTitle length={`${alternateDrawParticipants.length}`} text={title || t('alternates')} />
      )}
      {alternateDrawParticipants?.length === 0 ? (
        <CompetitorsTableEmptyState>
          <BodyLargeLight>{isTeamTable ? t('noAlternateTeams') : t('noAlternatePlayers')}</BodyLargeLight>
        </CompetitorsTableEmptyState>
      ) : (
        <>
          <CompetitorsTableSubtitle subtitle={t('alternatesTableInfo')} />

          {alternateDrawParticipants?.length > 0 ? (
            <DrawParticipantsTable
              isEnableMultiRowSelection
              additionalButtons={additionalButtons}
              columns={tableColumns}
              data={alternateDrawParticipants}
              isDouble={isDouble}
              isEditReorder={isEditReorder}
              saveColumnsSelection={saveColumnsSelection}
              savedColumns={columnsSelection}
              selectedActions={renderBulkActions}
              setEntryPositionsState={setEntryPositionsState}
              type={ALTERNATE}
            />
          ) : (
            <FilteringNoResults />
          )}
        </>
      )}
    </>
  );
};

export default AlternatesTable;
